/**
 * Router root.
 *
 * `BrowserRouter` (not `HashRouter`): the URLs must stay byte-identical to the
 * Next.js ones — `/dashboard`, `/rent/payments`, `/itr/{id}` — because they are
 * bookmarked, deep-linked from reminder emails, and matched by
 * `PAGE_REGISTRY.matchPrefix`. A hash router would change every one of them.
 * The cost is that the static host must rewrite unknown paths to `index.html`;
 * see `web/README.md` for the nginx line.
 *
 * `basename` comes from Vite's `BASE_URL` so the app works wherever it is
 * mounted. In dev that is `/`; in production it is currently `/app/`, because
 * the SPA runs ALONGSIDE the Next.js app it replaces rather than on top of it
 * (no sudo on the prod box, so nginx cannot be repointed yet). Without this,
 * Vite rewrites the asset URLs but the router still believes it is at the
 * domain root, so every path misses and the login page never renders —
 * exactly the symptom this fixes. When nginx finally serves the SPA at `/`,
 * BASE_URL becomes `/` and this needs no edit.
 */
import { BrowserRouter, useRoutes } from "react-router-dom";
import { Providers } from "@/components/Providers";
import { usePrivacyRevision } from "@/lib/privacy/store";
import { routes } from "@/routes";

/** `"/app/"` -> `"/app"`; `"/"` -> `""`. React Router wants no trailing slash. */
const BASENAME = import.meta.env.BASE_URL.replace(/\/$/, "");

/**
 * The route tree — and the app-wide repaint for hide-values (V5-03).
 *
 * `usePrivacyRevision()` subscribes to the privacy store and nothing else; the
 * value is deliberately unused. When the hide-values flag flips, this component
 * re-renders, `useRoutes` rebuilds the matched route elements, and every screen
 * below repaints — with `formatINR` and the module formatters now returning
 * masked strings. That is how one toggle reaches ~70 files that import nothing
 * from the privacy layer.
 *
 * It has to be HERE, not in a provider. A provider receives `children` as a
 * prop; that element reference does not change when the provider re-renders, so
 * React bails out of reconciling the subtree and nothing below repaints. A
 * component that CREATES the route elements each render does not have that
 * problem.
 */
function Routes() {
  usePrivacyRevision();
  return useRoutes(routes);
}

export function App() {
  return (
    <BrowserRouter basename={BASENAME}>
      <Providers>
        <Routes />
      </Providers>
    </BrowserRouter>
  );
}
